import React from "react";
import { Chip, Paper, Stack, Typography } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import _ from "lodash";
import SummaryChip from "../../components/SummaryChip";
import {
    CHANNEL_LABELS,
    CONDITION_ROLE_FILTER,
    CONDITION_ROLE_TRIGGER,
    actionSummary,
    conditionSummary,
    conditionsByRole,
    describeSchedule,
    formatDuration,
    formatRuleTimestamp,
} from "./alertRuleConstants";

// Chips de la tarjeta de una regla en la lista. `compact` es la versión para pantallas angostas (solo los números).
export const AlertRuleCardSummary = ({ item, compact = false }) => {
    const rule = item.data || {};
    const triggerConditions = conditionsByRole(rule.rule_conditions, CONDITION_ROLE_TRIGGER);
    const filterConditions = conditionsByRole(rule.rule_conditions, CONDITION_ROLE_FILTER);
    const recipients = rule.rule_recipients || [];
    const actions = rule.rule_device_actions || [];
    const cooldownLabel = formatDuration(rule.cooldown_seconds);
    const scheduleLabel = describeSchedule(rule);
    const plural = (count, singular, pluralLabel) => (compact ? `${count}` : `${count} ${count === 1 ? singular : pluralLabel}`);

    return (
        <>
            <SummaryChip icon={<BoltOutlinedIcon />} label={plural(triggerConditions.length, "trigger", "triggers")} color="warning" />
            {filterConditions.length > 0 && <SummaryChip icon={<FilterAltOutlinedIcon />} label={plural(filterConditions.length, "filtro", "filtros")} />}
            {recipients.length > 0 && <SummaryChip icon={<NotificationsActiveOutlinedIcon />} label={compact ? `${recipients.length}` : `${recipients.length} notif.`} color="primary" />}
            {actions.length > 0 && <SummaryChip icon={<PlayCircleOutlineOutlinedIcon />} label={plural(actions.length, "acción", "acciones")} color="success" />}
            <SummaryChip icon={<AccessTimeOutlinedIcon />} label={cooldownLabel} />
            {!compact && scheduleLabel && <SummaryChip icon={<EventAvailableOutlinedIcon />} label={scheduleLabel} />}
        </>
    );
};

export const alertRuleSubtitle = (item) => {
    const lastTriggeredLabel = formatRuleTimestamp(item.data?.last_triggered_at);
    return item.description || (lastTriggeredLabel ? `Último disparo: ${lastTriggeredLabel}` : "Sin disparos recientes");
};

const DetailBox = ({ title, children }) => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, flex: 1, bgcolor: "rgba(15, 23, 42, 0.02)" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>{title}</Typography>
        {children}
    </Paper>
);

// Detalle expandido: cuándo se gatilla, filtros y qué hace.
export const AlertRuleCardDetails = ({ item }) => {
    const rule = item.data || {};
    const triggerConditions = conditionsByRole(rule.rule_conditions, CONDITION_ROLE_TRIGGER);
    const filterConditions = conditionsByRole(rule.rule_conditions, CONDITION_ROLE_FILTER);
    const recipients = rule.rule_recipients || [];
    const actions = rule.rule_device_actions || [];

    return (
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ pt: 1.5 }}>
            <DetailBox title="Cuando">
                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mt: 1 }}>
                    {_.map(triggerConditions, (condition) => (
                        <Chip
                            key={`rule-detail-condition-${rule.id}-${condition?.id || condition?.position}`}
                            size="small"
                            variant="outlined"
                            label={conditionSummary(condition)}
                            sx={{ maxWidth: "100%" }}
                        />
                    ))}
                </Stack>
            </DetailBox>

            <DetailBox title="Y si">
                {!_.isEmpty(filterConditions) && (
                    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mt: 1 }}>
                        {_.map(filterConditions, (condition) => (
                            <Chip
                                key={`rule-detail-filter-${rule.id}-${condition?.id || condition?.position}`}
                                size="small"
                                variant="outlined"
                                label={conditionSummary(condition)}
                                sx={{ maxWidth: "100%" }}
                            />
                        ))}
                    </Stack>
                )}
            </DetailBox>

            <DetailBox title="Entonces">
                <Stack spacing={0.75} mt={1}>
                    {!_.isEmpty(recipients) && (
                        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                            {_.map(recipients, (recipient) => (
                                <Chip
                                    key={`rule-detail-recipient-${rule.id}-${recipient?.id || `${recipient?.user_id}-${recipient?.channel}`}`}
                                    size="small"
                                    color={recipient?.enabled ? "primary" : "default"}
                                    variant={recipient?.enabled ? "filled" : "outlined"}
                                    label={`${recipient?.user_name || `Usuario ${recipient?.user_id}`} · ${CHANNEL_LABELS[recipient?.channel] || recipient?.channel}`}
                                />
                            ))}
                        </Stack>
                    )}
                    {!_.isEmpty(actions) && (
                        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                            {_.map(actions, (action) => (
                                <Chip
                                    key={`rule-detail-action-${rule.id}-${action?.id || `${action?.target_device_id}-${action?.desired_state}`}`}
                                    size="small"
                                    color={action?.enabled ? "warning" : "default"}
                                    variant={action?.enabled ? "filled" : "outlined"}
                                    label={actionSummary(action)}
                                />
                            ))}
                        </Stack>
                    )}
                </Stack>
            </DetailBox>
        </Stack>
    );
};
